import {
    Accordion,
    AccordionItem,
    AccordionItemContent,
    AccordionItemHeader,
    Box,
    InfoTooltip,
} from "@edgetier/client-components";
import { Button, Spinner, SpinnerUntil, Tooltip } from "@edgetier/components";
import { doNothing } from "@edgetier/utilities";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { CSSProperties, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import AddItemButton from "./add-item-button";
import SelectBulkButtons from "./select-bulk-buttons";
import SelectListTitle from "./select-list-title";
import { IProps } from "./select-menu.types";
import SelectSelectedList from "./select-selected-list";
import { APPLY_BUTTON_TEXT, CANCEL_BUTTON_TEXT, MINIMUM_TAGS_TO_OPEN_ACCORDIONS } from "../select.constants";
import { calculateGroupedFlatItemIndex, isEndScroll } from "./select-menu.utilitites";
import { SelectMenuMessage } from "./select-menu-message";
import { SelectInfoMessage } from "./select-info-message";
import "./select-menu.scss";
import { SELECT_ALL_ITEMS_LENGTH_LIMIT } from "./select-menu.constants";

/**
 * Menu to allow users to choose one or more items from a list.
 * @param props.allItems                        An array of all the items, regardless of whether they are selected/unselected.
 * @param props.addItemMenu                     A menu for adding an item to the select.
 * @param props.addItemButtonProps              Props for the add item button.
 * @param props.children                        Optional method to render items.
 * @param props.close                           Method to close the menu.
 * @param props.description                     Description of items to be selected.
 * @param props.disableMenuItems                Whether the menu items should be disabled or not.
 * @param props.getGroup                        Getter for an item's group name.
 * @param props.getLabel                        Getter for an item's label.
 * @param props.getValue                        Getter for an item's value.
 * @param props.getIcon                         Getter for an item's icon.
 * @param props.isLoading                       Optional loading state of the items.
 * @param props.isSingleSelect                  Whether one or more items can be selected.
 * @param props.message                         An optional message to show in the select menu.
 * @param props.noItemsFoundLabel               Label to show when no items are found.
 * @param props.onSelectItems                   Handler when items are selected.
 * @param props.onMassSelect                    Handler when all items are selected or deselected.
 * @param props.onInputChange                   A function that gets called when the select's input value changes.
 * @param props.highlightedIndex                Index of the highlighted item.
 * @param props.getItemProps                    Getter for the props of an item.
 * @param props.getSelectedItemProps            Getter for the props of a selected item.
 * @param props.removeSelectedItem              Method to remove a selected item.
 * @param props.selectedItems                   Items selected.
 * @param props.setSelectedItems                Method to set the selected items.
 * @param props.notSelectedItems                Items not selected.
 * @param props.selectedValues                  Values selected before opening the menu.
 * @param props.selectedItemsLimit              Maximum number of items that can be selected.
 * @param props.infoMessage                     An optional configuration for the info message. Contains a callback to determine if the message should be shown and the message itself.
 * @param props.itemsSelectedBeforeOpen         The items that were selected before the select menu was opened.
 * @param props.virtualized                     If the lists should be virtualized.
 * @param props.itemheight                      The list item height, if virtualized.
 * @param props.titleSection                    An optional configuration for the title section. Title section won't be shown if not provided.
 * @param props.titleSection.title              The title being showed in the title section.
 * @param props.titleSection.tooltipContent     The tooltip content for the title section. Tooltip won't be shown if not provided.
 * @param props.hideBulkActionSelectAll         Whether to hide the "select all" bulk action button.
 * @param props.hideBulkActionDeselectAll       Whether to hide the "deselect all" bulk action button.
 * @param props.disableBulkActionSelectAll      Whether to disable the "select all" bulk action button.
 * @param props.disableBulkActionDeselectAll    Whether to disable the "deselect all" bulk action button.
 * @param props.selectAllDisabledTooltip        Tooltip to show when the "select all" button is disabled.
 * @param props.deselectAllDisabledTooltip      Tooltip to show when the "deselect all" button is disabled.
 * @param props.isPaginated                     Whether the list is paginated or not.
 * @param props.isNextPageLoading               Whether the next page is loading or not.
 * @param props.isNextPageError                 Whether the next page has an error or not.
 * @param props.onScrollEndReached              Callback to call when the end of the scroll is reached.
 * @param props.onRetry                         Callback to call when the retry button is clicked.
 * @param props.isSaving                        Whether the menu is in a saving state or not.
 * @param props.hasAsyncAction                  Whether the menu has an async action or not.
 * @param props.hasNextPage                     Whether the menu has a next page or not.
 * @param props.isMultipleSelectCheckbox        Whether the menu is a multiple select with checkboxes or not.
 * @param props.enableAutoFocus                 Whether the menu should be focused when opened.
 * @param props.messageTooltip                  Tooltip to show when the message is shown.
 * @param props.menuListProps                   Props for the menu list.
 * @param props.isCompact                       Whether the menu is compact or not.
 * @param props.isItemDisabled                  Function to determine if an item is disabled or not.
 * @returns Menu of items to choose.
 */
const SelectMenu = <IItem extends {}, IValue extends {} = string>({
    allItems,
    addItemMenu,
    addItemButtonProps,
    children,
    close,
    description,
    disableMenuItems = false,
    isItemDisabled,
    getGroup,
    getLabel,
    getValue,
    getIcon,
    isCompact = false,
    isLoading = false,
    isSingleSelect = false,
    message,
    noItemsFoundLabel,
    onSelectItems,
    onMassSelect = doNothing,
    highlightedIndex,
    getItemProps,
    getSelectedItemProps,
    removeSelectedItem,
    selectedItems,
    setSelectedItems,
    notSelectedItems,
    selectedValues,
    menuListProps: externalMenuListProps,
    isMultipleSelectCheckbox,
    enableAutoFocus = true,
    messageTooltip,
    selectedItemsLimit,
    infoMessage,
    itemsSelectedBeforeOpen = selectedItems, // Default to selectedItems as these will match most of the time.
    hideBulkActionSelectAll,
    hideBulkActionDeselectAll,
    disableBulkActionSelectAll,
    disableBulkActionDeselectAll,
    selectAllDisabledTooltip,
    deselectAllDisabledTooltip,
    virtualized,
    titleSection,
    isSaving,
    hasAsyncAction,
    hasNextPage,
    isNextPageLoading,
    isNextPageError,
    onScrollEndReached,
    onRetry,
    selectAllItemsLengthLimit,
    isPaginated,
    ...other
}: IProps<IItem, IValue>) => {
    const isMobileMultiSelect = isMultipleSelectCheckbox && !isSingleSelect;

    const menuListProps = useMemo(() => {
        return {
            ...externalMenuListProps,
            onScroll: (event: React.UIEvent<HTMLUListElement>) => {
                if (
                    !virtualized &&
                    isEndScroll(
                        event.currentTarget.scrollTop,
                        event.currentTarget.clientHeight,
                        event.currentTarget.scrollHeight
                    )
                ) {
                    onScrollEndReached?.();
                }
            },
        };
    }, [externalMenuListProps, onScrollEndReached, virtualized]);

    const { flatItems, flatItemIndexMap, groupedItemsWithIndex } = useMemo(
        () => calculateGroupedFlatItemIndex(notSelectedItems, getGroup),
        [notSelectedItems, getGroup]
    );

    const itemLimit = useMemo(
        () => (typeof selectedItemsLimit === "number" ? selectedItemsLimit : selectedItemsLimit?.limit),
        [selectedItemsLimit]
    );

    const limitMessage = useMemo(
        () => (typeof selectedItemsLimit === "number" ? `${description} limit reached` : selectedItemsLimit?.message),
        [selectedItemsLimit, description]
    );

    const limitReached = useMemo(
        () => (typeof itemLimit === "undefined" ? false : selectedItems.length >= itemLimit),
        [selectedItems, itemLimit]
    );

    const { flatItemIndexMap: mobileFlatItemIndexMap } = useMemo(() => {
        // Calculate grouped flat items for filtered items
        return calculateGroupedFlatItemIndex(notSelectedItems, getGroup);
    }, [getGroup, notSelectedItems]);

    const withoutMultipleGroupsItems = useMemo(() => {
        return Array.from(isMobileMultiSelect ? mobileFlatItemIndexMap.entries() : flatItemIndexMap.entries());
    }, [isMobileMultiSelect, mobileFlatItemIndexMap, flatItemIndexMap]);

    /**
     * Select some items and close the menu.
     * @param items Newly selected items.
     */
    const selectAndClose = (items: IItem[]) => {
        onSelectItems(items.map(getValue), items);
        !hasAsyncAction && close();
    };

    /**
     * Close the menu and return the selected options.
     */
    const onApply = (currentlySelectedItems: IItem[]) => {
        selectAndClose(currentlySelectedItems);
    };

    /**
     * Select all items.
     * If there is an item limit, select the first n items in the list
     */
    const onSelectAll = useCallback(() => {
        if (typeof itemLimit !== "undefined") {
            const itemsToSelect = notSelectedItems.slice(0, itemLimit - selectedItems.length);
            setSelectedItems(selectedItems.concat(itemsToSelect));
        } else {
            setSelectedItems(selectedItems.concat(notSelectedItems));
        }
        onMassSelect();
    }, [notSelectedItems, onMassSelect, selectedItems, itemLimit, setSelectedItems]);

    /**
     * Clear all items.
     */
    const onSelectNone = useCallback(() => {
        setSelectedItems([]);
        onMassSelect();
    }, [onMassSelect, setSelectedItems]);

    const hasMultipleGroups = useMemo(() => Object.keys(groupedItemsWithIndex).length > 1, [groupedItemsWithIndex]);

    /**
     * Create a list item to be shown in the menu.
     * @param item  Item to be shown.
     * @param index Index of the item.
     */
    const createListItem = useCallback(
        (item: IItem, index: number, style?: CSSProperties) => {
            const value = getValue(item);
            const key = typeof value === "object" ? Object.values(value).join() : value.toString();
            const isSelected = selectedItems.includes(item);

            return (
                <li key={key} data-testid={[other?.["data-testid"], key].filter(Boolean).join("_")} style={style}>
                    {!hasMultipleGroups &&
                        typeof getGroup === "function" &&
                        (index === 0 || getGroup(item) !== getGroup(notSelectedItems[index - 1])) && (
                            <div aria-label={getGroup(item)} className="select-menu__group-title">
                                {getGroup(item)}
                            </div>
                        )}
                    <div
                        aria-label={getLabel(item)}
                        className={classNames("select-menu__option", {
                            "select-menu__option--is-highlighted": index === highlightedIndex,
                            "select-menu__option--is-disabled":
                                isItemDisabled?.(item) || disableMenuItems || limitReached,
                        })}
                        {...getItemProps({ item, index: index })}
                    >
                        <div className="select-menu__option__label">
                            {typeof getIcon === "function" ? (
                                <span className="select-menu__option__label__icon">
                                    <FontAwesomeIcon icon={getIcon(item)} />
                                </span>
                            ) : null}
                            {isMobileMultiSelect ? (
                                <div className="select-menu__option-checkbox-container">
                                    <input type="checkbox" checked={isSelected} onChange={() => {}} />
                                    {typeof children === "function" ? children(item) : getLabel(item)}
                                </div>
                            ) : (
                                <>{typeof children === "function" ? children(item) : getLabel(item)}</>
                            )}
                        </div>
                        {!isSingleSelect && !isMultipleSelectCheckbox && <FontAwesomeIcon icon={faChevronRight} />}
                    </div>
                </li>
            );
        },
        [
            selectedItems,
            getGroup,
            getLabel,
            getValue,
            hasMultipleGroups,
            isItemDisabled,
            disableMenuItems,
            limitReached,
            highlightedIndex,
            isMobileMultiSelect,
            children,
            getItemProps,
            notSelectedItems,
            other,
            getIcon,
            isMultipleSelectCheckbox,
            isSingleSelect,
        ]
    );

    const hasNoItems = useMemo(
        () => notSelectedItems.length === 0 && selectedItems.length === 0,
        [notSelectedItems.length, selectedItems.length]
    );

    const multiSelectHasNoItems = useMemo(
        () => !isSingleSelect && notSelectedItems.length === 0,
        [isSingleSelect, notSelectedItems.length]
    );

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const onScrollIntoView = useCallback((index: number) => {
        if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({ index, align: "start", behavior: "smooth" });
        }
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                if (highlightedIndex !== null && highlightedIndex !== -1) {
                    onScrollIntoView(highlightedIndex);
                }
            }
        };

        if (virtualized) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [highlightedIndex, virtualized, onScrollIntoView]);

    const isSelectAllBulkActionDisabled = useMemo(
        () =>
            disableBulkActionSelectAll ||
            (typeof disableBulkActionSelectAll === "undefined" &&
                notSelectedItems.length + selectedItems.length >
                    (selectAllItemsLengthLimit ?? SELECT_ALL_ITEMS_LENGTH_LIMIT)),
        [disableBulkActionSelectAll, notSelectedItems, selectedItems, selectAllItemsLengthLimit]
    );

    const selectAllBulkActionTooltipText = useMemo(
        () =>
            selectAllDisabledTooltip || notSelectedItems.length === 0
                ? ""
                : `To improve performance, selecting all is not available when list exceeds ${selectAllItemsLengthLimit ?? SELECT_ALL_ITEMS_LENGTH_LIMIT} items.`,
        [selectAllDisabledTooltip, notSelectedItems.length, selectAllItemsLengthLimit]
    );

    return (
        <Box
            className={classNames("select-menu", {
                "select-menu--is-multiple-select": !isSingleSelect && !isMultipleSelectCheckbox,
                "select-menu--is-single-select": isSingleSelect,
            })}
        >
            {titleSection && (
                <div className="select-menu__title">
                    <h5>{titleSection.title}</h5>{" "}
                    {titleSection.tooltipContent && (
                        <InfoTooltip placement="left-center" content={titleSection.tooltipContent} />
                    )}
                </div>
            )}
            <div className={classNames("select-menu__lists", { "select-menu__virtualized-lists": virtualized })}>
                <div className="select-menu__list select-menu__list--not-selected">
                    {!isSingleSelect && !isMultipleSelectCheckbox && (
                        <SelectListTitle count={notSelectedItems.length} hasMore={hasNextPage} title="Not Selected" />
                    )}

                    <div className="select-menu__options">
                        {typeof message !== "undefined" &&
                            (typeof messageTooltip !== "undefined" ? (
                                <Tooltip content={messageTooltip} useArrow>
                                    <SelectMenuMessage message={message} />
                                </Tooltip>
                            ) : (
                                <SelectMenuMessage message={message} />
                            ))}

                        {/** Use itemsSelectedBeforeOpen here because that will be more up-to-date than selectedItems */}
                        {!isCompact && isSingleSelect && itemsSelectedBeforeOpen.length > 0 && (
                            <div className="select-menu__selected-item">
                                {typeof getIcon === "function" ? (
                                    <span className="select-menu__option__label__icon">
                                        <FontAwesomeIcon icon={getIcon(itemsSelectedBeforeOpen[0])} />
                                    </span>
                                ) : null}

                                <>
                                    {typeof children === "function"
                                        ? children(itemsSelectedBeforeOpen[0])
                                        : getLabel(itemsSelectedBeforeOpen[0])}
                                </>
                            </div>
                        )}

                        {!isLoading && (hasNoItems || multiSelectHasNoItems) && (
                            <div className="select-menu__options--empty">
                                {typeof noItemsFoundLabel === "undefined"
                                    ? `No ${description}s found`
                                    : noItemsFoundLabel}
                            </div>
                        )}

                        <SpinnerUntil data={[]} isReady={!isLoading}>
                            {!hasMultipleGroups ? (
                                virtualized ? (
                                    <ul className="select-menu__list" {...menuListProps}>
                                        <Virtuoso
                                            ref={virtuosoRef}
                                            className="select-menu__list-virtuoso"
                                            style={{ height: "100%" }}
                                            data={withoutMultipleGroupsItems}
                                            endReached={onScrollEndReached}
                                            itemContent={(_, [item, index]) => createListItem(item, index)}
                                            components={{
                                                Footer: () => (
                                                    <>
                                                        {(isNextPageLoading || isLoading) && <Spinner />}
                                                        {isNextPageError && (
                                                            <div className="select-menu__retry-button">
                                                                <Button onClick={onRetry}>Retry</Button>
                                                            </div>
                                                        )}
                                                    </>
                                                ),
                                            }}
                                        />
                                    </ul>
                                ) : (
                                    <ul className="select-menu__list" {...menuListProps}>
                                        {withoutMultipleGroupsItems.map(([item, index]) => createListItem(item, index))}
                                        {hasNextPage && (
                                            <div className="select-menu__load-more">
                                                {(isNextPageLoading || isLoading) && <Spinner />}
                                                {isNextPageError && (
                                                    <div className="select-menu__retry-button">
                                                        <Button onClick={onRetry}>Retry</Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </ul>
                                )
                            ) : (
                                <ul className="select-menu__list" {...menuListProps}>
                                    {Object.entries(groupedItemsWithIndex).map(([group, items]) => (
                                        <Accordion key={group}>
                                            <AccordionItem
                                                canOpen
                                                isOpen={flatItems.length < MINIMUM_TAGS_TO_OPEN_ACCORDIONS}
                                                key={group}
                                            >
                                                <AccordionItemHeader>
                                                    <div aria-label={group}>{group}</div>
                                                </AccordionItemHeader>
                                                <AccordionItemContent>
                                                    {items.map(({ item }, index) => {
                                                        const flatItemIndex = flatItemIndexMap.get(item);
                                                        return createListItem(item, flatItemIndex ?? index);
                                                    })}
                                                    {hasNextPage && (
                                                        <div className="select-menu__load-more">
                                                            {(isNextPageLoading || isLoading) && <Spinner />}
                                                            {isNextPageError && (
                                                                <div className="select-menu__retry-button">
                                                                    <Button onClick={onRetry}>Retry</Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </AccordionItemContent>
                                            </AccordionItem>
                                        </Accordion>
                                    ))}
                                </ul>
                            )}
                        </SpinnerUntil>
                    </div>

                    {typeof addItemMenu !== "undefined" && (
                        <div className="select-menu__add-item">
                            <AddItemButton addItemMenu={addItemMenu} {...addItemButtonProps} />
                        </div>
                    )}
                </div>
                {!isSingleSelect && !isMultipleSelectCheckbox && (
                    <SelectBulkButtons
                        isDisabled={disableMenuItems}
                        notSelectedItemsCount={notSelectedItems.length}
                        onSelectAll={onSelectAll}
                        onSelectNone={onSelectNone}
                        selectedItemsCount={selectedItems.length}
                        limitReached={limitReached}
                        hideBulkActionSelectAll={hideBulkActionSelectAll || isPaginated}
                        hideBulkActionDeselectAll={hideBulkActionDeselectAll}
                        disableBulkActionSelectAll={isSelectAllBulkActionDisabled}
                        selectAllDisabledTooltip={selectAllBulkActionTooltipText}
                    />
                )}

                {!isSingleSelect && !isMultipleSelectCheckbox && (
                    <SelectSelectedList
                        getGroup={getGroup}
                        getLabel={getLabel}
                        getSelectedItemProps={getSelectedItemProps}
                        getValue={getValue}
                        isLoading={isLoading}
                        removeSelectedItem={removeSelectedItem}
                        selectedItems={selectedItems}
                        itemLimit={itemLimit}
                        limitMessage={limitMessage}
                        virtualized={virtualized}
                    >
                        {children}
                    </SelectSelectedList>
                )}
            </div>

            {typeof infoMessage !== "undefined" &&
                (typeof infoMessage.showMessage === "undefined" || infoMessage.showMessage(selectedItems)) && (
                    <div
                        className={classNames("select-menu__info-message", {
                            "select-menu__info-message--is-multiple-select":
                                !isMultipleSelectCheckbox && !isSingleSelect,
                        })}
                    >
                        <SelectInfoMessage message={infoMessage.message} />
                    </div>
                )}

            {!isSingleSelect && (
                <div className="select-menu__controls">
                    {!isMultipleSelectCheckbox && (
                        <Button icon={faTimes} onClick={() => close()} styleName="neutral">
                            {CANCEL_BUTTON_TEXT}
                        </Button>
                    )}
                    <Button
                        disabled={
                            isSaving ||
                            isLoading ||
                            (typeof itemLimit !== "undefined" && selectedItems.length > itemLimit)
                        }
                        icon={faCheck}
                        isLoading={isSaving}
                        onClick={() => onApply(selectedItems)}
                        styleName={isMultipleSelectCheckbox ? "primary" : "positive"}
                        className={
                            isMultipleSelectCheckbox
                                ? "select-menu__controls-mobile-apply"
                                : "select-menu__controls-apply"
                        }
                    >
                        {APPLY_BUTTON_TEXT}
                    </Button>
                </div>
            )}
        </Box>
    );
};

export default memo(SelectMenu) as typeof SelectMenu;
